import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../../utils/api';
import { apiErrorMessage } from '../../../../utils/apiError';
import { EnterpriseEndpoints } from '../../../../utils/endpoints';
import { Permissions } from '../../../../utils/permissions';
import { formatCount, formatNaira } from '../../../../utils/money';
import { PAYOUT_REFUSAL_REASONS, PAYOUT_SUPPORT_REFUSALS, PAYOUT_THROTTLED } from '../../../../utils/constants';
import { useAuth } from '../../../../AuthContext';
import { Alert, Button, DataTable, DetailRow, Modal, Skeleton } from '../../../ui';
import { isEnterpriseDeactivated } from './EnterpriseDeactivated';
import StoreCell from '../dashboard/StoreCell';

const DEACTIVATED_MESSAGE = 'This account is deactivated. Contact CashAfrica support.';

const refusalCopy = (reason) => PAYOUT_REFUSAL_REASONS[reason] ?? 'This store cannot be paid out right now';

const storeCount = (count) => `${formatCount(count)} ${count === 1 ? 'store' : 'stores'}`;

const accountLine = (destination) =>
  [destination.bank_name ?? destination.bank_code, destination.account_number].filter(Boolean).join(' · ');

const quoteErrorMessage = (error) =>
  isEnterpriseDeactivated(error)
    ? DEACTIVATED_MESSAGE
    : apiErrorMessage(error, 'We could not load the payout quote. Please try again.');

const payoutErrorMessage = (error) => {
  if (error?.response?.status === 429) return PAYOUT_THROTTLED;
  if (isEnterpriseDeactivated(error)) return DEACTIVATED_MESSAGE;
  const message = apiErrorMessage(error, 'We could not start the payout. Please try again.');
  return PAYOUT_SUPPORT_REFUSALS.includes(message) ? `${message} Contact CashAfrica support.` : message;
};

const queuedMessage = ({ queued = 0, estimated_amount: estimatedAmount }) =>
  queued === 0
    ? 'No store had anything to pay out.'
    : `Payouts queued for ${storeCount(queued)}, an estimated ${formatNaira(estimatedAmount)} in total. They appear in the list as each one starts.`;

const MoneyGoesTo = ({ destination }) => (
  <div className="mb-4 rounded border border-theme-border px-4 py-3 dark:border-themedark-border">
    <div className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">Money goes to</div>
    <div className="text-lg font-semibold text-theme-headings dark:text-themedark-headings">
      {destination.account_name || '—'}
    </div>
    <div className="text-base text-theme-bodycolor dark:text-themedark-bodycolor">{accountLine(destination)}</div>
  </div>
);

const OneStoreQuote = ({ quote }) => (
  <>
    <DetailRow label="Balance" value={formatNaira(quote.balance)} />
    <DetailRow label="Amount to send" value={formatNaira(quote.amount)} />
    <DetailRow label="Fee" value={formatNaira(quote.fee)} />
    <DetailRow label="Stays in the wallet" value={formatNaira(quote.retained)} />
    {!quote.can_pay_out && (
      <Alert variant="warning" className="mt-4">
        {refusalCopy(quote.reason)}
      </Alert>
    )}
  </>
);

const AllStoresQuote = ({ quote, busy, onPayOut }) => {
  const columns = [
    { key: 'store', header: 'Store', render: (row) => <StoreCell store={row.store} /> },
    { key: 'balance', header: 'Balance', align: 'right', render: (row) => formatNaira(row.balance) },
    { key: 'amount', header: 'Amount to send', align: 'right', render: (row) => formatNaira(row.amount) },
    { key: 'fee', header: 'Fee', align: 'right', render: (row) => formatNaira(row.fee) },
    {
      key: 'action',
      header: '',
      align: 'right',
      render: (row) =>
        row.can_pay_out ? (
          <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => onPayOut(row)}>
            Pay out
          </Button>
        ) : (
          <span className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
            {refusalCopy(row.reason)}
          </span>
        ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={quote.stores ?? []}
        rowKey={(row) => row.store?.id}
        empty={{ title: 'No stores', message: 'Your enterprise has no stores to pay out.' }}
      />
      <div className="mt-4">
        <DetailRow label="Total to send" value={formatNaira(quote.total_amount)} />
        <DetailRow label="Total fees" value={formatNaira(quote.total_fee)} />
        <DetailRow label="Stores ready to pay out" value={formatCount(quote.payable_count)} />
      </div>
    </>
  );
};

const confirmCopy = (pending, destination) => {
  const to = destination ? accountLine(destination) : 'the enterprise settlement account';
  return pending.all
    ? `Pay out ${storeCount(pending.count)}, ${formatNaira(pending.amount)} in total, to ${to}?`
    : `Send ${formatNaira(pending.amount)} from ${pending.store.name} to ${to}?`;
};

const PayoutDialog = ({ isOpen, onClose, store, onPaidOut }) => {
  const { hasPermission } = useAuth();
  const canReadBankAccount = hasPermission(Permissions.enterpriseBankAccount);
  const storeId = store?.id;
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [bankAccount, setBankAccount] = useState(null);
  const [pending, setPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const latestQuote = useRef(0);
  const latestBankAccount = useRef(0);

  const loadQuote = useCallback(() => {
    const requestId = latestQuote.current + 1;
    latestQuote.current = requestId;
    setQuoteLoading(true);
    setQuoteError('');
    const request = storeId
      ? api.get(EnterpriseEndpoints.payoutQuote, { params: { merchant_id: storeId } })
      : api.get(EnterpriseEndpoints.payoutQuote);
    request
      .then((response) => {
        if (latestQuote.current === requestId) setQuote(response?.data?.data ?? null);
      })
      .catch((failure) => {
        if (latestQuote.current !== requestId) return;
        setQuote(null);
        setQuoteError(quoteErrorMessage(failure));
      })
      .finally(() => {
        if (latestQuote.current === requestId) setQuoteLoading(false);
      });
  }, [storeId]);

  // The one-store quote carries no destination, so it is read from the bank account instead.
  const loadBankAccount = useCallback(() => {
    const requestId = latestBankAccount.current + 1;
    latestBankAccount.current = requestId;
    setBankAccount(null);
    if (!storeId || !canReadBankAccount) return;
    api
      .get(EnterpriseEndpoints.bankAccount)
      .then((response) => {
        if (latestBankAccount.current === requestId) setBankAccount(response?.data?.data ?? null);
      })
      .catch(() => {});
  }, [storeId, canReadBankAccount]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setQuote(null);
    setPending(null);
    setError('');
    loadQuote();
    loadBankAccount();
    return () => {
      latestQuote.current += 1;
      latestBankAccount.current += 1;
    };
  }, [isOpen, loadQuote, loadBankAccount]);

  const destination = storeId ? bankAccount : quote?.destination;
  const busy = submitting || quoteLoading;

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      if (pending.all) {
        const response = await api.post(EnterpriseEndpoints.payoutsAll);
        onPaidOut(queuedMessage(response?.data?.data ?? {}));
      } else {
        const response = await api.post(EnterpriseEndpoints.payouts, { merchant_id: pending.store.id });
        const payout = response?.data?.data ?? {};
        onPaidOut(`Payout of ${formatNaira(payout.amount)} from ${payout.store?.name ?? pending.store.name} initiated.`);
      }
      setSubmitting(false);
      onClose();
    } catch (failure) {
      setSubmitting(false);
      setPending(null);
      setError(payoutErrorMessage(failure));
      if (failure?.response?.status === 422) {
        loadQuote();
        loadBankAccount();
      }
    }
  };

  const payOutStore = (row) => setPending({ store: row.store, amount: row.amount });

  const actions = !quote ? null : storeId ? (
    <Button disabled={busy || !quote.can_pay_out} onClick={() => payOutStore({ ...quote, store: quote.store ?? store })}>
      Pay out {formatNaira(quote.amount)}
    </Button>
  ) : (
    <Button
      disabled={busy || !quote.payable_count}
      onClick={() => setPending({ all: true, count: quote.payable_count, amount: quote.total_amount })}
    >
      Pay out all ({storeCount(quote.payable_count ?? 0)})
    </Button>
  );

  const footer = pending ? (
    <>
      <p className="mr-auto font-medium text-theme-headings dark:text-themedark-headings">
        {confirmCopy(pending, destination)}
      </p>
      <Button variant="secondary" disabled={submitting} onClick={() => setPending(null)}>
        Back
      </Button>
      <Button loading={submitting} onClick={submit}>
        Confirm
      </Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={close}>
        Cancel
      </Button>
      {actions}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={store ? `Pay out ${store.name}` : 'Pay out stores'}
      size={store ? 'md' : 'lg'}
      footer={footer}
    >
      {error && (
        <Alert variant="danger" className="mb-4" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      {quoteError && (
        <Alert variant="danger" className="mb-4">
          {quoteError}{' '}
          <button type="button" className="font-medium underline" onClick={loadQuote}>
            Try again
          </button>
        </Alert>
      )}
      {destination && <MoneyGoesTo destination={destination} />}
      {store && <StoreCell store={quote?.store ?? store} />}
      {quoteLoading && !quote && <Skeleton w="100%" h="8rem" />}
      {quote &&
        (storeId ? (
          <OneStoreQuote quote={quote} />
        ) : (
          <AllStoresQuote quote={quote} busy={busy} onPayOut={payOutStore} />
        ))}
    </Modal>
  );
};

export default PayoutDialog;
